/**
 * peerStatsParser — Turn an RTCStatsReport into one normalized, UI-friendly
 * sample for a peer that mostly RECEIVES media (robot camera/mic -> browser).
 *
 * Pure functions, no React, no RTCPeerConnection: easy to unit test.
 *
 * Why not the old PeerStats/_collectStats code: it read the `track` stats type
 * (removed in Chrome 112) and `remote-inbound-rtp.roundTripTime` (only exists
 * for streams the browser SENDS), so jitter-buffer delay, decoded frames and
 * RTT were always -1 for the robot video. The receiver-side sources are:
 *   - delay  : candidate-pair.currentRoundTripTime of the selected pair (STUN RTT)
 *   - jitter : inbound-rtp.jitter (seconds -> ms)
 *   - buffer : inbound-rtp.jitterBufferDelay / jitterBufferEmittedCount (seconds -> ms)
 *   - frames : inbound-rtp.framesDecoded / framesPerSecond / freezeCount ...
 * Rates (fps, bitrate, loss %) are deltas against the previous sample.
 */

const sec2ms = (s) => (typeof s === "number" && Number.isFinite(s) ? s * 1000 : null);
const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** Index an RTCStatsReport (or any iterable of stats objects) by id and type. */
const indexReport = (report) => {
  const byId = new Map();
  const byType = new Map();
  const add = (s) => {
    if (!s || typeof s !== "object" || !s.type) return;
    if (s.id) byId.set(s.id, s);
    if (!byType.has(s.type)) byType.set(s.type, []);
    byType.get(s.type).push(s);
  };
  if (report && typeof report.forEach === "function") {
    report.forEach((value) => add(value));
  } else if (Array.isArray(report)) {
    report.forEach(add);
  }
  return { byId, byType: (t) => byType.get(t) || [] };
};

/**
 * Pick the candidate pair media actually flows over.
 * Prefers transport.selectedCandidatePairId, then a nominated+succeeded pair,
 * then the succeeded pair with the most bytes.
 */
export const selectCandidatePair = (idx) => {
  for (const t of idx.byType("transport")) {
    const pair = t.selectedCandidatePairId && idx.byId.get(t.selectedCandidatePairId);
    if (pair) return pair;
  }
  const pairs = idx.byType("candidate-pair");
  const nominated = pairs.find((p) => p.nominated && p.state === "succeeded");
  if (nominated) return nominated;
  const succeeded = pairs.filter((p) => p.state === "succeeded");
  if (succeeded.length === 0) return null;
  return succeeded.reduce((best, p) => ((p.bytesReceived || 0) + (p.bytesSent || 0) > (best.bytesReceived || 0) + (best.bytesSent || 0) ? p : best));
};

const describePath = (idx, pair) => {
  if (!pair) return null;
  const local = pair.localCandidateId ? idx.byId.get(pair.localCandidateId) : null;
  const remote = pair.remoteCandidateId ? idx.byId.get(pair.remoteCandidateId) : null;
  const localType = local?.candidateType || null;
  const remoteType = remote?.candidateType || null;
  return {
    local: localType,
    remote: remoteType,
    protocol: local?.protocol || remote?.protocol || null,
    relayed: localType === "relay" || remoteType === "relay",
  };
};

/** Primary inbound-rtp of a kind: the one with the most bytes (ignores idle RTX/FEC ssrcs). */
const primaryInbound = (idx, kind) => {
  const list = idx.byType("inbound-rtp").filter((s) => s.kind === kind || s.mediaType === kind);
  if (list.length === 0) return null;
  return list.reduce((best, s) => ((s.bytesReceived || 0) > (best.bytesReceived || 0) ? s : best));
};

const codecName = (idx, stat) => {
  const codec = stat?.codecId ? idx.byId.get(stat.codecId) : null;
  if (!codec?.mimeType) return null;
  const i = codec.mimeType.indexOf("/");
  return i >= 0 ? codec.mimeType.slice(i + 1) : codec.mimeType;
};

const delta = (cur, prev) => (cur == null || prev == null ? null : cur - prev);

/** Average of an accumulated-seconds counter over a count, as ms; interval first, cumulative fallback. */
const avgDelayMs = (cur, prev, delayKey, countKey) => {
  if (!cur) return null;
  const dDelay = prev ? delta(num(cur[delayKey]), num(prev[delayKey])) : null;
  const dCount = prev ? delta(num(cur[countKey]), num(prev[countKey])) : null;
  if (dDelay != null && dCount != null && dCount > 0) return (dDelay / dCount) * 1000;
  const total = num(cur[delayKey]);
  const count = num(cur[countKey]);
  if (total != null && count != null && count > 0) return (total / count) * 1000;
  return null;
};

const lossPct = (cur, prev) => {
  if (!cur) return null;
  const lost = prev ? delta(num(cur.packetsLost), num(prev.packetsLost)) : num(cur.packetsLost);
  const received = prev ? delta(num(cur.packetsReceived), num(prev.packetsReceived)) : num(cur.packetsReceived);
  if (lost == null || received == null) return null;
  const total = received + Math.max(lost, 0);
  if (total <= 0) return 0;
  return (Math.max(lost, 0) / total) * 100;
};

const bitrateKbps = (bytesNow, bytesPrev, dtMs) => {
  if (bytesNow == null || bytesPrev == null || !dtMs || dtMs <= 0) return null;
  return ((bytesNow - bytesPrev) * 8) / dtMs; // bytes/ms*8 == kbit/s
};

/**
 * Parse one stats report.
 *
 * @param {RTCStatsReport|Array} report
 * @param {Object|null} prev - previous sample returned by this function (for rates)
 * @param {number} [nowMs=Date.now()] - fallback timestamp
 * @returns {Object} sample
 */
export const parseStatsReport = (report, prev = null, nowMs = Date.now()) => {
  const idx = indexReport(report);
  const pair = selectCandidatePair(idx);
  const inVideo = primaryInbound(idx, "video");
  const inAudio = primaryInbound(idx, "audio");
  const prevVideo = prev?.raw?.video || null;
  const prevAudio = prev?.raw?.audio || null;
  const prevPair = prev?.raw?.pair || null;

  const timestamp = num(inVideo?.timestamp) ?? num(inAudio?.timestamp) ?? num(pair?.timestamp) ?? nowMs;
  const dtMs = prev?.timestamp != null ? timestamp - prev.timestamp : null;

  // --- delay (RTT) ---
  let rttMs = sec2ms(num(pair?.currentRoundTripTime));
  if (rttMs == null && pair && prevPair) {
    const dRtt = delta(num(pair.totalRoundTripTime), num(prevPair.totalRoundTripTime));
    const dResp = delta(num(pair.responsesReceived), num(prevPair.responsesReceived));
    if (dRtt != null && dResp != null && dResp > 0) rttMs = (dRtt / dResp) * 1000;
  }

  // --- video ---
  let video = null;
  if (inVideo) {
    const framesDecoded = num(inVideo.framesDecoded);
    const framesDecodedDelta = prevVideo ? delta(framesDecoded, num(prevVideo.framesDecoded)) : null;
    let fps = num(inVideo.framesPerSecond);
    if (fps == null && framesDecodedDelta != null && dtMs > 0) fps = (framesDecodedDelta / dtMs) * 1000;
    const bytesReceived = num(inVideo.bytesReceived);
    video = {
      codec: codecName(idx, inVideo),
      jitterMs: sec2ms(num(inVideo.jitter)),
      jitterBufferDelayMs: avgDelayMs(inVideo, prevVideo, "jitterBufferDelay", "jitterBufferEmittedCount"),
      fps,
      framesDecoded,
      framesDecodedDelta,
      framesReceived: num(inVideo.framesReceived),
      framesDropped: num(inVideo.framesDropped),
      frameWidth: num(inVideo.frameWidth),
      frameHeight: num(inVideo.frameHeight),
      packetsReceived: num(inVideo.packetsReceived),
      packetsLost: num(inVideo.packetsLost),
      packetLossPct: lossPct(inVideo, prevVideo),
      bytesReceived,
      bytesReceivedDelta: prevVideo ? delta(bytesReceived, num(prevVideo.bytesReceived)) : null,
      bitrateKbps: prevVideo ? bitrateKbps(bytesReceived, num(prevVideo.bytesReceived), dtMs) : null,
      freezeCount: num(inVideo.freezeCount),
      totalFreezesDurationMs: sec2ms(num(inVideo.totalFreezesDuration)),
      nackCount: num(inVideo.nackCount),
      pliCount: num(inVideo.pliCount),
      lastPacketReceivedTimestamp: num(inVideo.lastPacketReceivedTimestamp),
      decoderImplementation: inVideo.decoderImplementation || null,
    };
  }

  // --- audio ---
  let audio = null;
  if (inAudio) {
    const bytesReceived = num(inAudio.bytesReceived);
    audio = {
      codec: codecName(idx, inAudio),
      jitterMs: sec2ms(num(inAudio.jitter)),
      jitterBufferDelayMs: avgDelayMs(inAudio, prevAudio, "jitterBufferDelay", "jitterBufferEmittedCount"),
      packetsReceived: num(inAudio.packetsReceived),
      packetsLost: num(inAudio.packetsLost),
      packetLossPct: lossPct(inAudio, prevAudio),
      bytesReceived,
      bitrateKbps: prevAudio ? bitrateKbps(bytesReceived, num(prevAudio.bytesReceived), dtMs) : null,
      audioLevel: num(inAudio.audioLevel),
      concealedSamples: num(inAudio.concealedSamples),
      concealmentEvents: num(inAudio.concealmentEvents),
    };
  }

  // --- outbound (local mic to robot speaker) ---
  let bytesSent = 0;
  let hasOutbound = false;
  for (const o of idx.byType("outbound-rtp")) {
    hasOutbound = true;
    bytesSent += o.bytesSent || 0;
  }
  const outbound = hasOutbound
    ? {
        bytesSent,
        bitrateKbps: prev?.outbound?.bytesSent != null ? bitrateKbps(bytesSent, prev.outbound.bytesSent, dtMs) : null,
      }
    : null;

  return {
    timestamp,
    intervalMs: dtMs,
    rttMs,
    path: describePath(idx, pair),
    availableIncomingBitrateKbps: num(pair?.availableIncomingBitrate) != null ? pair.availableIncomingBitrate / 1000 : null,
    availableOutgoingBitrateKbps: num(pair?.availableOutgoingBitrate) != null ? pair.availableOutgoingBitrate / 1000 : null,
    video,
    audio,
    outbound,
    // raw counters kept for the next delta computation only
    raw: { video: inVideo || null, audio: inAudio || null, pair: pair || null },
  };
};

/**
 * Quality grade from the receiver's point of view, following the usual
 * teleoperation thresholds (RTT and jitter in ms, loss in %).
 *   good     : rtt < 150, jitter < 30, loss < 1
 *   warning  : rtt < 300, jitter < 60, loss < 3
 *   serious  : rtt < 600, jitter < 120, loss < 8
 *   critical : anything worse
 * Returns null when nothing is measurable yet.
 */
export const gradeSample = (sample) => {
  if (!sample) return null;
  const rtt = sample.rttMs;
  const jitter = sample.video?.jitterMs ?? sample.audio?.jitterMs ?? null;
  const loss = sample.video?.packetLossPct ?? sample.audio?.packetLossPct ?? null;
  if (rtt == null && jitter == null && loss == null) return null;
  const level = (v, g, w, s) => (v == null ? 0 : v < g ? 0 : v < w ? 1 : v < s ? 2 : 3);
  const worst = Math.max(level(rtt, 150, 300, 600), level(jitter, 30, 60, 120), level(loss, 1, 3, 8));
  return ["good", "warning", "serious", "critical"][worst];
};

export const STALL_SAMPLES = 3;

/**
 * Media health of a video peer, from the last few samples and the track state.
 *   'ok'       frames are being decoded
 *   'stalled'  a video track exists but no new frame for STALL_SAMPLES samples (or the track is muted)
 *   'no-track' the connection is up but no remote video track arrived
 *   'unknown'  not connected / no data yet
 * `previous` gives hysteresis: an 'ok' peer stays 'ok' until STALL_SAMPLES
 * consecutive samples without a decoded frame prove the stall.
 */
export const computeVideoHealth = ({ connected, expectsVideo, hasVideoTrack, trackMuted, samples, previous = "unknown" }) => {
  if (!connected) return "unknown";
  if (!hasVideoTrack) return expectsVideo ? "no-track" : "unknown";
  if (trackMuted) return "stalled";
  const inconclusive = previous === "ok" ? "ok" : "unknown";
  const recent = (samples || []).slice(-STALL_SAMPLES).filter((s) => s && s.video);
  if (recent.length === 0) return inconclusive;
  const measured = recent.filter((s) => s.video.framesDecodedDelta != null);
  if (measured.length === 0) {
    // first sample: judge by whether anything was ever decoded
    const last = recent[recent.length - 1];
    return (last.video.framesDecoded || 0) > 0 ? "ok" : inconclusive;
  }
  const anyFrames = measured.some((s) => s.video.framesDecodedDelta > 0);
  if (anyFrames) return "ok";
  return measured.length >= STALL_SAMPLES ? "stalled" : inconclusive;
};
